import React, { useMemo, useRef, useState } from 'react';
import { Plane } from 'lucide-react';
import clsx from 'clsx';
import { openJourneyPlanner } from './journeyPlannerEvents';

const randomFloat = (min = 0, max = 1) => {
  return +(min + Math.random() * (max - min)).toFixed(2);
};

const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);

const createSmokeParticles = (count = 18) => {
  return Array.from({ length: count }, (_, i) => {
    const progress = i / Math.max(count - 1, 1);
    const eased = easeOutCubic(progress);

    return {
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
      x: randomFloat(-8, -2),
      y: randomFloat(-8, 8),
      driftX: randomFloat(-18, -34) * (0.75 + eased * 0.25),
      driftY: randomFloat(-14, 14),
      scaleFrom: randomFloat(0.65, 0.95),
      scaleTo: randomFloat(1.4, 2.4),
      duration: randomFloat(480, 900),
      delay: randomFloat(0, 180),
      opacity: randomFloat(0.22, 0.5),
    };
  });
};

const JourneyButton = ({
  children = 'Plan Your Journey',
  className,
  onClick,
  to,
  as: Component = 'button',
  disabled = false,
  ...props
}) => {
  const [isFlying, setIsFlying] = useState(false);
  const [burstKey, setBurstKey] = useState(0);
  const timeoutRef = useRef(null);

  const particles = useMemo(() => {
    return createSmokeParticles(18);
  }, [burstKey]);

  const handleClick = (e) => {
    if (disabled) return;

    e?.preventDefault?.();
    e?.stopPropagation?.();

    setBurstKey((prev) => prev + 1);
    setIsFlying(true);
    openJourneyPlanner();

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsFlying(false);
    }, 900);

    if (onClick) onClick(e);
  };

  const sharedClassName = clsx(
    'journey-btn group relative inline-flex items-center justify-center overflow-hidden rounded-2xl',
    'bg-gold-500 px-6 py-3.5 text-base font-semibold tracking-subtle text-[#07201e] shadow-gold',
    'transition-all duration-200 hover:-translate-y-0.5 hover:bg-gold-600 hover:shadow-elevated',
    'focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:ring-offset-2',
    'shrink-0 whitespace-nowrap',
    'disabled:cursor-not-allowed disabled:opacity-60',
    className
  );

  const content = (
    <>
      <span className="relative z-[2]">{children}</span>

      <span className="journey-btn__icon-wrap" aria-hidden="true">
        <span className={clsx('journey-btn__smoke-zone', isFlying && 'is-active')}>
          {particles.map((particle) => (
            <span
              key={particle.id}
              className="journey-btn__smoke"
              style={{
                '--smoke-x': `${particle.x}px`,
                '--smoke-y': `${particle.y}px`,
                '--smoke-drift-x': `${particle.driftX}px`,
                '--smoke-drift-y': `${particle.driftY}px`,
                '--smoke-scale-from': particle.scaleFrom,
                '--smoke-scale-to': particle.scaleTo,
                '--smoke-duration': `${particle.duration}ms`,
                '--smoke-delay': `${particle.delay}ms`,
                '--smoke-opacity': particle.opacity,
              }}
            />
          ))}
        </span>

        <Plane
          className={clsx('journey-btn__plane', isFlying && 'is-flying')}
          strokeWidth={2}
        />
      </span>
    </>
  );

  if (Component === 'button') {
    return (
      <button
        type="button"
        className={sharedClassName}
        onClick={handleClick}
        disabled={disabled}
        {...props}
      >
        {content}
      </button>
    );
  }

  return (
    <Component
      className={sharedClassName}
      onClick={handleClick}
      to={to}
      {...props}
    >
      {content}
    </Component>
  );
};

export default JourneyButton;
